import { useState } from 'react'
import { Button, Description, Input, TextField } from '@heroui/react'
import { phrases, t, type Language } from '../lib/kioskText'
import { wordingProblem } from '../lib/wording'
import { useStaffText } from '../ui/StaffText'
import type { LayoutDocument, LayoutElement } from './types'
import { DESIGN_WORDS, DESIGN_WORD_NAMES, MAX_WORD_LENGTH, ownWord, withWord, wordsOf, type DesignWordId } from './words'

/** A field's name, with the language it edits when there is more than one. */
export function textLabel(name: string, language: string, several: boolean): string {
  return several ? `${name} · ${language.toUpperCase()}` : name
}

/** The words an element draws as it is set: a list that skips the first is blank when
 *  nobody has been called, and one without desks names none. */
export function elementWords(element: LayoutElement): DesignWordId[] {
  const ids = wordsOf(element.kind)
  if (element.kind !== 'calls') return ids
  return ids.filter((id) =>
    id === 'deskLabel' ? element.options?.showDesk !== false : !(id === 'boardNoCalls' && (element.options?.skip ?? 0) > 0),
  )
}

/** The built-in words an element draws: this design's own, in the chosen language. */
export function WordFields({
  document: doc,
  element,
  language,
  onReplace,
}: {
  document: LayoutDocument
  element: LayoutElement
  language: string
  onReplace: (doc: LayoutDocument) => void
}) {
  const ids = elementWords(element)
  if (ids.length === 0) return null
  return (
    <>
      {ids.map((id) => (
        <WordField key={`${id}:${language}`} id={id} document={doc} language={language} onReplace={onReplace} />
      ))}
    </>
  )
}

function WordField({
  id,
  document: doc,
  language,
  onReplace,
}: {
  id: DesignWordId
  document: LayoutDocument
  language: string
  onReplace: (doc: LayoutDocument) => void
}) {
  const { say } = useStaffText()
  const own = ownWord(doc.words, id, language)
  const organisation = t(id, language as Language)
  const optional = DESIGN_WORDS.find((w) => w.id === id)?.optional
  // Held here only while it could not be kept (empty, or a placeholder lost).
  const [held, setHeld] = useState<string | null>(null)
  const problem = held === null ? null : wordingProblem(held, phrases[id].en, MAX_WORD_LENGTH, optional)
  const labelId = `insp-word-${id}`
  const names = (list: readonly string[]) => list.map((n) => `{${n}}`).join(' ')

  const change = (text: string) => {
    if (wordingProblem(text, phrases[id].en, MAX_WORD_LENGTH, optional)) {
      setHeld(text)
      return
    }
    setHeld(null)
    // The organisation's word stays the organisation's, and follows it when it changes.
    onReplace(withWord(doc, id, language, text.trim() === organisation.trim() ? '' : text))
  }

  return (
    <TextField
      className="studio-field"
      aria-labelledby={labelId}
      value={held ?? own ?? organisation}
      isInvalid={!!problem}
      onChange={change}
      onBlur={() => setHeld(null)}
    >
      <span className="studio-between">
        <span className="studio-field__label" id={labelId}>{say(DESIGN_WORD_NAMES[id]!)}</span>
        {own !== undefined && (
          <Button
            variant="ghost"
            size="sm"
            onPress={() => {
              setHeld(null)
              onReplace(withWord(doc, id, language, ''))
            }}
          >
            {say('designs.word.reset')}
          </Button>
        )}
      </span>
      <Input variant="secondary" />
      {problem?.kind === 'placeholders' && <small className="studio-field__error">{say('wording.problem.placeholders', { names: names(problem.need) })}</small>}
      {problem?.kind === 'onlyPlaceholders' && <small className="studio-field__error">{say('wording.problem.onlyPlaceholders', { names: names(problem.may) })}</small>}
      {optional && !problem && <Description>{say('wording.mayUse', { names: names(optional) })}</Description>}
    </TextField>
  )
}
